import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import { Text, TextLink } from '@lightspeed/flame/Text';
import Axios from 'axios';

import BusinessSnapshot from 'root/apps/BusinessSnapshot/BusinessSnapshot';
import { CapitalOffer } from 'root/apps/BusinessSnapshot/BusinessSnapshot.types';
import Modules from 'root/apps/shared_modules/Modules';
import { VisibilityTracker } from 'root/apps/tracking/VisibilityTracker';
import { datadogRum } from 'root/datadog/datadogRum';

import { Message } from '../components/Message/Message';
import { CapitalBannerLocations, CapitalBannerVersions, CapitalTrackingEvents } from '../enums';
import { useCapitalTracking } from '../hooks';
import { UseOfferData } from '../hooks/useOfferData';
import { CapitalBannerBone, CapitalBannerCard, CapitalContainer, CapitalImage } from '../styles';

const OFFER_URL = '/admin/payments/lightspeed-payments/offer';
const LSPD_CAPITAL_URL = '/payments/lightspeed-payments/merchant-portal/capital';

type Props = {
  capitalOffer?: CapitalOffer;
  snapshotDismissalValue?: string;
  withVisibilityTracker?: boolean;
  offerData: UseOfferData;
};

export const CapitalHeroBanner: React.FC<Props> = ({
  capitalOffer,
  snapshotDismissalValue,
  withVisibilityTracker = false,
  offerData,
}) => {
  const {
    hasBannerRights,
    isLoading,
    offerAmount,
    offerId,
    setShowOffer,
    showOffer,
    daysUntilExpiration,
    expiryDate,
    chartType,
  } = offerData;
  const source = 'reports';
  const { trackCapitalEvent } = useCapitalTracking({ source });
  const [dismissed, setDismissed] = useState(false);
  const bannerLocation = CapitalBannerLocations.Top;
  const bannerVersion = CapitalBannerVersions.V2;
  const hasReportsCapitalHeapErrorBoundary = Modules.hasModule(
    'reports.capital.heap_error_boundary'
  );

  const navigateToLightspeedCapital = () => async (event: React.MouseEvent) => {
    event.preventDefault();

    trackCapitalEvent(CapitalTrackingEvents.CapitalBannerClicked, {
      banner_location: bannerLocation,
      banner_version: bannerVersion,
      offer_id: offerId,
      button_type: 'view',
    });
    window.PageRouter.goTo(LSPD_CAPITAL_URL);
  };

  const dismissBanner = async () => {
    const data: any = {
      isHidden: true,
      UUID: offerId,
    };

    try {
      await Axios.put(OFFER_URL, data);
    } catch (error) {
      datadogRum.addError(error, { message: 'Failed to hide Capital banner', ...data });
    }

    trackCapitalEvent(CapitalTrackingEvents.CapitalBannerClicked, {
      banner_location: bannerLocation,
      banner_version: bannerVersion,
      offer_id: offerId,
      button_type: 'dismiss',
      dismissal_type: 'new_offer',
    });
    setShowOffer(false);
    setDismissed(true);
    setInterval(() => setDismissed(false), 6000);
  };

  if (capitalOffer && chartType !== 'none') {
    const domNode = document.querySelector('[data-react-root="business_snapshot"]');
    if (domNode) {
      return createPortal(
        <BusinessSnapshot
          offer={capitalOffer}
          dismissalValue={snapshotDismissalValue}
          chartType={chartType}
        />,
        domNode as Element
      );
    }
  }

  if (!hasBannerRights) {
    return null;
  }

  if (isLoading) {
    return (
      <CapitalBannerCard data-test="capital-banner-loading-state">
        <Flex alignItems="center" flexDirection="row" width="100%">
          <Box p={2} pr={4}>
            <CapitalBannerBone width="90px" height="90px" p={2} pr={4} />
          </Box>
          <Flex
            flexDirection="column"
            justifyContent="space-evenly"
            width="60%"
            height="100%"
            py={1}
          >
            <CapitalBannerBone width="80%" height="24px" my={1} />
            <CapitalBannerBone width="80%" height="24px" my={1} />
            <CapitalBannerBone width="80%" height="24px" my={1} />
          </Flex>
        </Flex>
      </CapitalBannerCard>
    );
  }

  if (dismissed) {
    return (
      <CapitalBannerCard>
        <CapitalContainer
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          width="100%"
        >
          <Box px={4}>
            <Text as="div" textAlign="center">
              We’ll remind you when a new offer is available.
            </Text>
            <Text as="div">
              You can always check for available offers in your{' '}
              <TextLink href={LSPD_CAPITAL_URL} onClick={navigateToLightspeedCapital()}>
                Lightspeed Capital
              </TextLink>{' '}
              page.
            </Text>
          </Box>
        </CapitalContainer>
      </CapitalBannerCard>
    );
  }

  const trackedBannerContent = (
    <CapitalBannerCard data-test="capital-banner-v2">
      <Flex flexDirection="row" alignItems="center" width="100%">
        <Box height="120px">
          <CapitalImage src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/payments/capital-banner-hero.png" />
        </Box>
        <Flex
          flexDirection={['column', 'row']}
          width={1}
          justifyContent="space-between"
          alignItems={['start', 'center']}
          my="3"
          mx={['4', '0']}
        >
          <Message
            offerAmount={offerAmount}
            daysLeft={daysUntilExpiration}
            expiryDate={expiryDate}
          />
          <Flex flexDirection={['row-reverse', 'column']} my={[2, 0]} mr="3">
            <Button
              variant="secondary"
              fill
              mb={[0, 2]}
              width="148px"
              p={1}
              onClick={navigateToLightspeedCapital()}
              data-test="view-offer-button"
            >
              View your offer <IconArrowRight name="arrow-right" color="blue" />
            </Button>
            <Button fill onClick={dismissBanner}>
              Maybe Later
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </CapitalBannerCard>
  );

  if (showOffer && offerId) {
    if (withVisibilityTracker || !hasReportsCapitalHeapErrorBoundary) {
      return (
        <VisibilityTracker
          id="capital-banner-v2"
          eventName={CapitalTrackingEvents.CapitalBannerViewed}
          triggerOnce={true}
          eventData={{
            source,
            banner_location: bannerLocation,
            banner_version: bannerVersion,
            offer_id: offerId,
          }}
        >
          {trackedBannerContent}
        </VisibilityTracker>
      );
    }
    return trackedBannerContent;
  }
  return null;
};
